import { Coins, Gift, Package, Sparkles, Trophy } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import type { RewardCardReward } from "@/components/coins/reward-card"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { cn } from "@/lib/utils"

/**
 * OKS-556 — §19 flagship hero (UX Spec §2.2). The crew's single most
 * aspirational reward, spotlighted above the catalogue. The flagship is
 * DERIVED server-side as the highest-cost AVAILABLE reward (TRD KD-3), so it
 * always renders in an `available` state; the parent omits this block entirely
 * when the brand has no available reward (`flagship == null`).
 *
 * # Whole hero is the redeem affordance
 *
 * Mirroring `RewardCard`, the ENTIRE hero is a single click/keyboard target
 * that opens the §20 `RedeemSheet` (via the shared `onRedeem` contract) — for
 * BOTH the affordable case (sheet → Confirm) and the not-yet case (sheet →
 * can't-afford view). The flagship is always `available`, so it's always
 * openable.
 *
 * # NO `data-reward-id` on the hero (deliberate)
 *
 * The hero is a FEATURED DUPLICATE of a reward that also renders as its
 * canonical card in the category section below. The card carries the
 * `data-reward-id`; the hero deliberately does NOT — so each reward-id
 * resolves to exactly one element on the page (the OKS-557 E2E clicks
 * `[data-reward-id="<id>"]` and requires a single match). The hero stays a
 * tap target for UX (opens the same sheet) without polluting the id space.
 *
 * By affordability (UX §2.2): affordable → a "Redeem" cue; not-yet → a
 * progress bar (balance / cost, CSS-animated) + "N coins to go". Status is
 * conveyed via text + `aria-label`, never colour alone (UX §6).
 */

const KIND_ICON: Record<string, typeof Gift> = {
  product: Package,
  experience: Sparkles,
  custom: Gift,
}

interface FlagshipHeroProps {
  reward: RewardCardReward
  balance: number
  onRedeem: (reward: RewardCardReward) => void
}

export function FlagshipHero({ reward, balance, onRedeem }: FlagshipHeroProps) {
  const { t } = useTranslation()

  const affordable =
    reward.affordable && !reward.out_of_stock && !reward.pending
  const pct =
    reward.coin_cost <= 0
      ? 100
      : Math.min(100, Math.round((balance / reward.coin_cost) * 100))

  const handleActivate = () => onRedeem(reward)

  return (
    <section
      data-testid="flagship-hero"
      role="button"
      tabIndex={0}
      aria-label={t("rewards.creator.card.redeem_aria", {
        title: reward.title,
        count: reward.coin_cost,
      })}
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          handleActivate()
        }
      }}
      className="bg-card hover:border-primary/40 focus-visible:ring-ring cursor-pointer overflow-hidden rounded-2xl border transition-colors focus-visible:ring-2 focus-visible:outline-none"
    >
      <FlagshipCover reward={reward} />

      <div className="flex flex-col gap-3 p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Badge
            className="gap-1 border-amber-300 bg-amber-100 text-amber-900"
            data-testid="flagship-hero-badge"
          >
            <Trophy className="size-3" aria-hidden="true" />
            {t("rewards.creator.flagship.label")}
          </Badge>
          <Badge
            variant="outline"
            className="font-normal"
            data-category={reward.category}
          >
            {t(`rewards.creator.categories.${reward.category}`)}
          </Badge>
        </div>

        <h2 className="text-lg font-semibold" data-testid="flagship-hero-title">
          {reward.title}
        </h2>

        <span
          className="inline-flex items-center gap-1.5 text-xl font-bold text-amber-700 tabular-nums"
          data-testid="flagship-hero-cost"
        >
          <Coins className="size-5 text-amber-700" aria-hidden="true" />
          {t("rewards.creator.card.coin_count", { count: reward.coin_cost })}
        </span>

        {affordable ? (
          <span
            className="text-primary text-sm font-semibold"
            data-testid="flagship-hero-redeem-cue"
          >
            {t("rewards.creator.flagship.redeem")}
          </span>
        ) : (
          <div
            className="flex flex-col gap-1.5"
            data-testid="flagship-hero-progress"
          >
            <Progress
              value={pct}
              aria-label={t("rewards.creator.flagship.progress_aria", {
                value: pct,
              })}
              className="[&>*]:transition-[width] [&>*]:duration-700 [&>*]:ease-out"
            />
            <span className="text-muted-foreground text-sm font-medium">
              {t("rewards.creator.flagship.coins_to_go", {
                count: reward.needs_more,
              })}
            </span>
          </div>
        )}
      </div>
    </section>
  )
}

/**
 * OKS-837 — the hero band is 640×160 CSS px (`container max-w-2xl px-4`
 * → 672 − 2×16 = 640 content px; height locked by `h-40`), i.e. **4:1**.
 *
 * It reads `cover_banner_url` (the 1280×320 `:banner` variant — 2× the box,
 * same ratio) and NOT `cover_image_url` (the 160² `:card` variant, which
 * feeds the 80px catalogue tile and the 64px sheet tile). Serving `:card`
 * here upscaled it ~4× horizontally and cropped it twice, nested — the
 * OKS-837 bug.
 *
 * Because the variant ratio equals the render box, `object-cover` is a pure
 * scale rather than a second crop, so the source's full horizontal extent
 * is visible. Below a 640px viewport (375px → a 343×160 box) it does
 * centre-crop horizontally; that is expected and accepted — a height-locked
 * hero must lose horizontal extent as the viewport narrows.
 *
 * The `?? cover_image_url` fallback is DEFENSIVE only: the two keys are
 * non-null together (one server-side precedence chain), so it is
 * unreachable in practice. Kept per UX Spec §2 state 3 to guard a client
 * holding pre-deploy props during a rolling deploy.
 */
function FlagshipCover({ reward }: { reward: RewardCardReward }) {
  const [errored, setErrored] = useState(false)
  const KindIcon = KIND_ICON[reward.kind] ?? Gift
  const coverUrl = reward.cover_banner_url ?? reward.cover_image_url
  const showImage = Boolean(coverUrl) && !errored

  if (showImage) {
    return (
      <img
        src={coverUrl!}
        alt={reward.title}
        loading="lazy"
        onError={() => setErrored(true)}
        data-testid="flagship-hero-cover"
        className="h-40 w-full object-cover"
      />
    )
  }

  return (
    <div
      aria-hidden="true"
      data-testid="flagship-hero-cover-fallback"
      className={cn(
        "flex h-40 w-full items-center justify-center",
        "bg-gradient-to-br from-amber-100 to-orange-200 text-amber-700",
      )}
    >
      <KindIcon className="size-12" />
    </div>
  )
}
